import type { EndpointsRegistry, FetchClient } from '~/api/types'
import { GatewayEndpoints } from '~/api/endpoints/gateway-endpoints'
import type { GenericObject, SuccessNoContent } from '~/types/common'
import type { ConfigurationPresetsService } from '~/api/services/types'
import type { PresetValues } from '~/types/asset-configuration'
import type { ConfigurationPreset, ConfigurationPresetPayload } from '~/types/configurationPreset'
import { isGroupPresetMocked } from '~/composables/useFeatureFlags'
import type { AssetsSTC } from '~/types/capabilities'
import { isGatewayAssetTypeId } from '~/utils/gateway.utils'
import { resolveAssetTypeScheduleFeatures } from '~/utils/schedules/schedule-features'

export interface ConfigurationPresetResponse {
  id: number
  name: string
  asset_type_id: string
  values: PresetValues | null
  used_by_groups?: number
  used_by?: { id: number; name: string }[]
  updated_at?: string
}

export const mapConfigurationPreset = (preset: ConfigurationPresetResponse): ConfigurationPreset => ({
  id: preset.id,
  name: preset.name,
  assetTypeId: preset.asset_type_id,
  values: preset.values ?? {},
  usedByGroups: preset.used_by_groups ?? 0,
  ...(preset.used_by && { usedBy: preset.used_by.map(({ id, name }) => ({ id, name })) }),
  ...(preset.updated_at && { updatedAt: preset.updated_at })
})

const toRequestBody = (payload: ConfigurationPresetPayload): GenericObject => ({
  name: payload.name,
  asset_type_id: payload.assetTypeId,
  values: payload.values
})

export const registerEndpoints = (client: FetchClient, registry: EndpointsRegistry): ConfigurationPresetsService => {
  return {
    getConfigurationPresets: async (assetTypeId?: string) => {
      if (isGroupPresetMocked) {
        const mock = await import('~/mocks/groupPresetMock')
        if (assetTypeId) return mock.listPresets(assetTypeId)
        const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.STATES_AND_TELEMETRY)
        const stc = await client<AssetsSTC>(url, { method, headers, body })
        const behaviourTypes = (stc?.data ?? [])
          .map((entry) => entry.assetTypeId)
          .filter((assetTypeId) => !isGatewayAssetTypeId(assetTypeId))
          .filter((assetTypeId) => resolveAssetTypeScheduleFeatures(stc, assetTypeId).isLegacySchedule === false)
        return mock.listPresets(undefined, behaviourTypes)
      }
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.CONFIGURATION_PRESETS_GET_ALL, {
        queryParams: assetTypeId ? { asset_type_id: assetTypeId } : {}
      })
      const result = await client<ConfigurationPresetResponse[] | null>(url, { method, headers, body })
      return (result ?? []).map(mapConfigurationPreset)
    },

    createConfigurationPreset: async (payload: ConfigurationPresetPayload) => {
      if (isGroupPresetMocked) {
        const mock = await import('~/mocks/groupPresetMock')
        return mock.createPreset(payload)
      }
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.CONFIGURATION_PRESETS_CREATE,
        {},
        toRequestBody(payload)
      )
      const result = await client<ConfigurationPresetResponse>(url, { method, headers, body })
      return mapConfigurationPreset(result)
    },

    updateConfigurationPreset: async (id: number, payload: ConfigurationPresetPayload) => {
      if (isGroupPresetMocked) {
        const mock = await import('~/mocks/groupPresetMock')
        return mock.updatePreset(id, payload)
      }
      const { url, method, headers, body } = registry.createRequest(
        GatewayEndpoints.CONFIGURATION_PRESETS_UPDATE,
        { params: { id } },
        toRequestBody(payload)
      )
      const result = await client<ConfigurationPresetResponse>(url, { method, headers, body })
      return mapConfigurationPreset(result)
    },

    deleteConfigurationPreset: async (id: number, force = false) => {
      if (isGroupPresetMocked) {
        const mock = await import('~/mocks/groupPresetMock')
        mock.deletePreset(id)
        return {} as SuccessNoContent
      }
      const { url, method, headers, body } = registry.createRequest(GatewayEndpoints.CONFIGURATION_PRESETS_DELETE, {
        params: { id },
        ...(force && { queryParams: { force: 'true' } })
      })
      return await client<SuccessNoContent>(url, { method, headers, body })
    }
  }
}
